import { codeFont } from '../../utils/fonts';
import { formatLaborHours } from '../../utils/laborTime';
import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import BuildIcon from '@mui/icons-material/Build';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BoltIcon from '@mui/icons-material/Bolt';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import JobFormDialog from './JobFormDialog';
import JobDetailDialog from './JobDetailDialog';
import {
  useListPreferences,
  FilterButton,
  FilterPanel,
  FilterSection,
  FilterChip,
  SearchInput,
  FilterSummary,
} from '../shared/ListFilters';
import DataTable from '../shared/DataTable';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import { extractListFromResponse } from '../../utils/apiUtils';
import { useUserStore } from '../../store';

const STORAGE_KEY = 'jobs_list_preferences';

const defaultPreferences = {
  sortField: 'name',
  sortDirection: 'asc',
  filters: {
    category: 'all', // all, uncategorized, or category id
    priceType: 'all', // all, fixed, hourly, variable
    hasDuration: 'all', // all, yes, no
    hasPrice: 'all', // all, yes, no
    quickSelect: 'all', // all, yes
  },
  searchQuery: '',
};

// Marks a job pinned to the work order's Add job dialog.
function QuickSelectMark({ t }) {
  return (
    <Tooltip title={t('Quick select')}>
      <BoltIcon
        data-testid="job-quick-select-mark"
        aria-label={t('Quick select')}
        sx={{ fontSize: 18, color: 'secondary.main', flexShrink: 0 }}
      />
    </Tooltip>
  );
}

const priceTypeLabels = {
  fixed: 'Fixed',
  hourly: 'Hourly',
  variable: 'Variable',
};

const MainJobsView = ({ t }) => {
  const { currentOrganization } = useUserStore();
  const showInclVat = currentOrganization?.default_price_includes_vat ?? false;
  const [categories, setCategories] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuJob, setMenuJob] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);

  const {
    preferences,
    updatePreferences,
    updateFilters: updateLocalFilters,
    resetFilters: resetLocalFilters,
    handleSort: handleLocalSort,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // Build server-side filter params from local preferences
  const serverFilters = useMemo(() => {
    const filters = {};

    // Category filter
    if (preferences.filters.category === 'uncategorized') {
      filters.uncategorized = true;
    } else if (preferences.filters.category !== 'all') {
      filters.category = preferences.filters.category;
    }

    // Price type filter
    if (preferences.filters.priceType !== 'all') {
      filters.price_type = preferences.filters.priceType;
    }

    // Has duration filter
    if (preferences.filters.hasDuration === 'yes') {
      filters.has_duration = true;
    } else if (preferences.filters.hasDuration === 'no') {
      filters.has_duration = false;
    }

    // Has price filter
    if (preferences.filters.hasPrice === 'yes') {
      filters.has_price = true;
    } else if (preferences.filters.hasPrice === 'no') {
      filters.has_price = false;
    }

    if (preferences.filters.quickSelect === 'yes') {
      filters.is_quick_select = true;
    }

    return filters;
  }, [preferences.filters]);

  // Map local sort field to server field
  const serverSortField = useMemo(() => {
    const sortMap = {
      name: 'name',
      category: 'category__name',
      duration: 'estimated_duration',
      price_type: 'price_type',
      price: 'price',
    };
    return sortMap[preferences.sortField] || 'name';
  }, [preferences.sortField]);

  // Use paginated list hook for server-side data fetching
  // Pass initial values from localStorage to prevent double request on mount
  const {
    data: jobs,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    setSearchQuery,
    setFilters: setServerFilters,
    setSort,
    refetch,
  } = usePaginatedList('/api/jobs/', {
    initialFilters: serverFilters,
    defaultSortField: serverSortField,
    defaultSortDirection: preferences.sortDirection,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Sync local preferences to server filters
  useEffect(() => {
    setServerFilters(serverFilters);
  }, [serverFilters, setServerFilters]);

  // Sync sort from local preferences to server
  useEffect(() => {
    setSort(serverSortField, preferences.sortDirection);
  }, [serverSortField, preferences.sortDirection, setSort]);

  // Sync search query from preferences to hook
  useEffect(() => {
    setSearchQuery(preferences.searchQuery);
  }, [preferences.searchQuery, setSearchQuery]);

  // Fetch categories separately (they don't need pagination)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get('/api/job-categories/');
        const { data } = extractListFromResponse(response);
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Handle sort - update local preferences (which triggers server sort via effect)
  const handleSort = useCallback((field) => {
    handleLocalSort(field);
  }, [handleLocalSort]);

  // Update filters in local preferences (which triggers server filter via effect)
  const updateFilters = useCallback((newFilters) => {
    updateLocalFilters(newFilters);
  }, [updateLocalFilters]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    resetLocalFilters();
  }, [resetLocalFilters]);

  const handleMenuOpen = (event, job) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuJob(job);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuJob(null);
  };

  const handleRowClick = (job) => {
    setSelectedJob(job);
    setDetailDialogOpen(true);
  };

  const handleEdit = () => {
    setSelectedJob(menuJob);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    if (menuJob) {
      setJobToDelete(menuJob);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (jobToDelete) {
      try {
        await axios.delete(`/api/jobs/${jobToDelete.id}/`);
        refetch();
      } catch (err) {
        console.error('Error deleting job:', err);
      }
    }
    setDeleteDialogOpen(false);
    setJobToDelete(null);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setJobToDelete(null);
  };

  const handleJobCreated = (newJob) => {
    refetch();
  };

  const handleJobUpdated = (updatedJob) => {
    refetch();
  };

  const handleCategoryCreated = (newCategory) => {
    setCategories([...categories, newCategory]);
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (preferences.filters.category !== 'all') count++;
    if (preferences.filters.priceType !== 'all') count++;
    if (preferences.filters.hasDuration !== 'all') count++;
    if (preferences.filters.hasPrice !== 'all') count++;
    if (preferences.filters.quickSelect === 'yes') count++;
    return count;
  }, [preferences.filters]);

  // Build filter summary chips
  const filterSummaryChips = useMemo(() => {
    const chips = [];

    if (preferences.filters.category === 'uncategorized') {
      chips.push({ key: 'category', label: t('Uncategorized') });
    } else if (preferences.filters.category !== 'all') {
      const cat = categories.find((c) => c.id === parseInt(preferences.filters.category, 10));
      if (cat) chips.push({ key: 'category', label: cat.name });
    }

    if (preferences.filters.priceType !== 'all') {
      chips.push({ key: 'priceType', label: t(priceTypeLabels[preferences.filters.priceType] || preferences.filters.priceType) });
    }

    if (preferences.filters.hasDuration === 'yes') {
      chips.push({ key: 'duration', label: t('Has duration') });
    } else if (preferences.filters.hasDuration === 'no') {
      chips.push({ key: 'duration', label: t('No duration') });
    }

    if (preferences.filters.hasPrice === 'yes') {
      chips.push({ key: 'price', label: t('Has price') });
    } else if (preferences.filters.hasPrice === 'no') {
      chips.push({ key: 'price', label: t('No price') });
    }

    if (preferences.filters.quickSelect === 'yes') {
      chips.push({ key: 'quickSelect', label: t('Quick selects') });
    }

    return chips;
  }, [preferences.filters, categories, t]);

  const formatPrice = (price, priceType) => {
    if (price === null || price === undefined) return '-';
    const formatted = new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
    if (priceType === 'hourly') {
      return `${formatted}/${t('h')}`;
    }
    return formatted;
  };

  // Stored in minutes, shown in decimal hours: 90 -> "1,5 h"
  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    return t('laborTime.hours', { hours: formatLaborHours(minutes) });
  };

  // State for error snackbar - cleared when error changes to null
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const prevError = React.useRef(error);

  React.useEffect(() => {
    // Show snackbar when a new error appears
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
  };

  return (
    <Box>
      {/* Error Snackbar - shows error without blocking the UI */}
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
          {t('Error')}: {error}
        </Alert>
      </Snackbar>
      {/* Header - Responsive */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('Jobs')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'text.secondary',
              mt: 0.5,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {t('Manage your job types and labor pricing')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => {
            setSelectedJob(null);
            setAddDialogOpen(true);
          }}
          startIcon={<AddIcon />}
          sx={{
            bgcolor: 'secondary.main',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderRadius: '10px',
            fontWeight: 500,
            fontSize: '14px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
            '&:hover': {
              bgcolor: 'secondary.light',
              boxShadow: '0 6px 16px rgba(49, 130, 206, 0.4)',
            },
          }}
        >
          {t('Add Job')}
        </Button>
      </Box>

      {/* Search and Filter Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={t('Search by name, code or description...')}
        />
        <FilterButton
          t={t}
          activeCount={activeFilterCount}
          isOpen={filtersOpen}
          onClick={toggleFiltersOpen}
        />
      </Box>

      {/* Active Filter Summary */}
      <FilterSummary chips={filterSummaryChips} onClear={resetFilters} t={t} />

      {/* Filter Panel */}
      <FilterPanel
        open={filtersOpen}
        resultCount={totalCount}
        onReset={resetFilters}
        resetDisabled={activeFilterCount === 0}
        t={t}
      >
        {/* Category Filter */}
        {categories.length > 0 && (
          <FilterSection title={t('Category')}>
            <FilterChip
              label={t('All')}
              active={preferences.filters.category === 'all'}
              onClick={() => updateFilters({ category: 'all' })}
              color="primary"
            />
            {categories.map((category) => (
              <FilterChip
                key={category.id}
                label={category.name}
                active={preferences.filters.category === String(category.id)}
                onClick={() => updateFilters({ category: String(category.id) })}
                color="primary"
              />
            ))}
            <FilterChip
              label={t('Uncategorized')}
              active={preferences.filters.category === 'uncategorized'}
              onClick={() => updateFilters({ category: 'uncategorized' })}
              color="warning"
            />
          </FilterSection>
        )}

        <FilterSection title={t('Price Type')} dividerTop={categories.length > 0}>
          <FilterChip
            label={t('Fixed')}
            active={preferences.filters.priceType === 'fixed'}
            onClick={() => updateFilters({ priceType: preferences.filters.priceType === 'fixed' ? 'all' : 'fixed' })}
            color="primary"
          />
          <FilterChip
            label={t('Hourly')}
            active={preferences.filters.priceType === 'hourly'}
            onClick={() => updateFilters({ priceType: preferences.filters.priceType === 'hourly' ? 'all' : 'hourly' })}
            color="info"
          />
          <FilterChip
            label={t('Variable')}
            active={preferences.filters.priceType === 'variable'}
            onClick={() => updateFilters({ priceType: preferences.filters.priceType === 'variable' ? 'all' : 'variable' })}
            color="secondary"
          />
        </FilterSection>

        <FilterSection title={t('Quick Filters')} dividerTop>
          <FilterChip
            icon={<AccessTimeIcon />}
            label={t('Has duration')}
            active={preferences.filters.hasDuration === 'yes'}
            onClick={() => updateFilters({ hasDuration: preferences.filters.hasDuration === 'yes' ? 'all' : 'yes' })}
            color="primary"
          />
          <FilterChip
            icon={<AccessTimeIcon />}
            label={t('No duration')}
            active={preferences.filters.hasDuration === 'no'}
            onClick={() => updateFilters({ hasDuration: preferences.filters.hasDuration === 'no' ? 'all' : 'no' })}
            color="warning"
          />
          <FilterChip
            icon={<AttachMoneyIcon />}
            label={t('Has price')}
            active={preferences.filters.hasPrice === 'yes'}
            onClick={() => updateFilters({ hasPrice: preferences.filters.hasPrice === 'yes' ? 'all' : 'yes' })}
            color="success"
          />
          <FilterChip
            icon={<AttachMoneyIcon />}
            label={t('No price')}
            active={preferences.filters.hasPrice === 'no'}
            onClick={() => updateFilters({ hasPrice: preferences.filters.hasPrice === 'no' ? 'all' : 'no' })}
            color="warning"
          />
          <FilterChip
            icon={<BoltIcon />}
            label={t('Quick selects')}
            active={preferences.filters.quickSelect === 'yes'}
            onClick={() => updateFilters({ quickSelect: preferences.filters.quickSelect === 'yes' ? 'all' : 'yes' })}
            color="secondary"
          />
        </FilterSection>
      </FilterPanel>

      {/* Desktop Table */}
      <DataTable
        columns={[
          {
            key: 'name',
            label: t('Job'),
            sortable: true,
            maxWidth: 350,
            render: (job) => (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                    {job.name}
                  </Typography>
                  {job.is_quick_select && <QuickSelectMark t={t} />}
                  {job.job_code && (
                    <Typography
                      sx={{
                        fontSize: '12px',
                        ...codeFont,
                        color: 'primary.main',
                        bgcolor: 'primary.50',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: '4px',
                      }}
                    >
                      {job.job_code}
                    </Typography>
                  )}
                </Box>
                {job.description && (
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '350px',
                      mt: 0.25,
                    }}
                  >
                    {job.description}
                  </Typography>
                )}
              </Box>
            ),
          },
          {
            key: 'category',
            label: t('Category'),
            sortable: true,
            render: (job) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {job.category_name || '-'}
              </Typography>
            ),
          },
          {
            key: 'duration',
            label: t('Duration'),
            sortable: true,
            width: '1%',
            noWrap: true,
            render: (job) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {job.estimated_duration && (
                  <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                )}
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  {formatDuration(job.estimated_duration)}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'price_type',
            label: t('Price Type'),
            sortable: true,
            width: '1%',
            noWrap: true,
            render: (job) => (
              <Chip
                label={t(priceTypeLabels[job.price_type] || 'Fixed')}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '11px',
                  height: '22px',
                  borderColor: job.price_type === 'hourly' ? 'info.main' : 'divider',
                  color: job.price_type === 'hourly' ? 'info.main' : 'text.secondary',
                }}
              />
            ),
          },
          {
            key: 'price',
            label: `${t('Price')} (${showInclVat ? t('incl. VAT') : t('excl. VAT')})`,
            sortable: true,
            align: 'right',
            width: '1%',
            noWrap: true,
            render: (job) => (
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                {formatPrice(showInclVat ? (job.price_incl_vat ?? job.price) : job.price, job.price_type)}
              </Typography>
            ),
          },
          {
            key: 'actions',
            label: '',
            width: '1%',
            render: (job) => (
              <IconButton
                onClick={(e) => handleMenuOpen(e, job)}
                sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ),
          },
        ]}
        data={jobs}
        loading={loading}
        onRowClick={handleRowClick}
        sortField={preferences.sortField}
        sortDirection={preferences.sortDirection}
        onSort={handleSort}
        emptyIcon={<BuildIcon />}
        emptyTitle={preferences.searchQuery || activeFilterCount > 0 ? t('No jobs match your criteria') : t('No jobs yet')}
        emptyDescription={t('Add job types to use them in maintenances')}
        testIdPrefix="job-row"
        tableTestId="jobs-table"
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Mobile Cards - Hidden on desktop */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {/* Loading overlay for refetch */}
        {loading && jobs.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && jobs.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : jobs.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <BuildIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              {preferences.searchQuery || activeFilterCount > 0
                ? t('No jobs match your criteria')
                : t('No jobs yet')}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t('Add job types to use them in maintenances')}
            </Typography>
          </Paper>
        ) : (
          jobs.map((job) => (
            <Paper
              key={job.id}
              elevation={0}
              onClick={() => handleRowClick(job)}
              sx={{
                p: 2,
                mb: 1.5,
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
                  {/* Job Icon */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: 'rgba(49, 130, 206, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'secondary.main',
                      flexShrink: 0,
                    }}
                  >
                    <BuildIcon />
                  </Box>

                  {/* Job Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography
                        sx={{
                          fontSize: '15px',
                          fontWeight: 600,
                          color: 'text.primary',
                        }}
                      >
                        {job.name}
                      </Typography>
                      {job.is_quick_select && <QuickSelectMark t={t} />}
                      {job.job_code && (
                        <Typography
                          sx={{
                            fontSize: '11px',
                            ...codeFont,
                            color: 'primary.main',
                            bgcolor: 'primary.50',
                            px: 0.5,
                            py: 0.125,
                            borderRadius: '4px',
                          }}
                        >
                          {job.job_code}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={t(priceTypeLabels[job.price_type] || 'Fixed')}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '10px',
                          height: '20px',
                          borderColor: job.price_type === 'hourly' ? 'info.main' : 'divider',
                          color: job.price_type === 'hourly' ? 'info.main' : 'text.secondary',
                        }}
                      />
                      {job.estimated_duration && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                            {formatDuration(job.estimated_duration)}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {job.category_name && (
                      <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                        {job.category_name}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Price and Actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'text.primary' }}>
                    {formatPrice(showInclVat ? (job.price_incl_vat ?? job.price) : job.price, job.price_type)}
                  </Typography>
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, job)}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                    size="small"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      {/* Mobile Pagination Controls */}
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          loading={loading}
        />
      </Box>

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {t('Edit')}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          {t('Delete')}
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <JobFormDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onJobCreated={handleJobCreated}
        onCategoryCreated={handleCategoryCreated}
        categories={categories}
      />

      <JobFormDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        job={selectedJob}
        onJobUpdated={handleJobUpdated}
        onCategoryCreated={handleCategoryCreated}
        categories={categories}
      />

      <JobDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        job={selectedJob}
        onEdit={() => {
          setDetailDialogOpen(false);
          setEditDialogOpen(true);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>{t('Delete job?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Are you sure you want to delete "{{name}}"? This action cannot be undone.', { name: jobToDelete?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>{t('Cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withTranslation()(MainJobsView);
